import type {
  WorkbookCell,
  WorkbookResult,
  WorkbookColumnarSheet,
} from '@erp/shared-types';
import { cellFilterOf } from './WorkbookCellMenu';
import type { CellFilter, PendingFilter } from './worksheetValueList';

type Filter = WorkbookColumnarSheet['filters'][number];

/**
 * Filter changes made on the shown Result, which the grid hints at until a
 * Result computed with them arrives. Kept for that `result` and while the
 * Worksheet's filters are still `filters`, the ones the changes left.
 */
export interface PendingFilters {
  result: WorkbookResult;
  filters: string;
  items: PendingFilter[];
}

const filtersKey = (filters: readonly Filter[]) => JSON.stringify(filters);

/**
 * Adds a filter change made on `result`, from `before` to `after`. Earlier
 * changes carry over only while they were made on the same Result and
 * nothing else has changed the filters since.
 */
export function addPendingFilter(
  current: PendingFilters | null,
  pending: PendingFilter,
  result: WorkbookResult,
  before: readonly Filter[],
  after: readonly Filter[],
): PendingFilters {
  const carried =
    current?.result === result && current.filters === filtersKey(before)
      ? current.items
      : [];
  return {
    result,
    filters: filtersKey(after),
    items: [...carried, pending],
  };
}

/**
 * The pending filter changes to hint at: none once the shown Result is
 * another one, or Current, or the filters have changed some other way.
 */
export function shownPendingFilters(
  current: PendingFilters | null,
  sheet: WorkbookColumnarSheet,
  result: WorkbookResult | undefined,
  stale: boolean,
): readonly PendingFilter[] {
  if (
    !current ||
    !stale ||
    current.result !== result ||
    current.filters !== filtersKey(sheet.filters)
  )
    return [];
  return current.items;
}

/** Whether a cell, as its Cell menu reads it, is `match`. A cut cell is no value the menu quotes. */
function cellIs(match: CellFilter, cell: CellFilter | null): boolean {
  if (!cell) return false;
  if (match.kind === 'value')
    return cell.kind === 'value' && cell.value === match.value;
  if (match.kind === 'errors')
    return cell.kind === 'errors' || cell.kind === 'na';
  return cell.kind === match.kind;
}

/**
 * Whether a row of the shown Result will be filtered out by a pending filter
 * change: its cell is the filtered-out one, or isn't the one kept. Only
 * compares already computed cells; the next Result says what really goes.
 */
export function willBeFilteredOut(
  pending: readonly PendingFilter[],
  columns: readonly string[],
  cells: readonly WorkbookCell[],
): boolean {
  return pending.some(({ column, cell, keep }) => {
    const index = columns.indexOf(column);
    const value = cells[index];
    if (index < 0 || value === undefined) return false;
    return cellIs(cell, cellFilterOf(value)) !== keep;
  });
}
